import type React from "react";
import { useId, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Info } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { findings as sharedFindings, insights, type reportingPeriod } from "@duress/shared";
import { fmtHours, fmtPct, fmtInt } from "@/lib/format";
import { BRAND, PILL_TEXT } from "@/lib/colors";
import { useChartTheme } from "@/lib/chart";
import { cn } from "@/lib/utils";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { TierBadge } from "../dashboard/TierBadge";
import { useDashboardView, type SelectedView } from "@/features/periods/usePeriods";
import { IndustryBenchmark } from "@/features/insights/IndustryBenchmark";
import { useBenchmark } from "@/features/insights/useInsights";
import { bandFor, BAND_META, type HealthBand } from "@/features/overview/health";

type KPIs = reportingPeriod.KPIs;
type StoreMetrics = reportingPeriod.StoreMetrics;
type PriorityTiers = reportingPeriod.PriorityTiers;
type ActivityTier = reportingPeriod.ActivityTier;
type ActivityTierBreakdown = reportingPeriod.ActivityTierBreakdown;

// ── Activity tier pill — same "colored pill" visual language as the L7/L28
// daily-activity tiers in TeamActivity.tsx, applied to the new recurrence
// tiers so the two related concepts read as one visual system. ──────────────

const ACTIVITY_TIER_LABELS: Record<ActivityTier, string> = {
  highly_active: "Highly Active",
  active: "Active",
  intermittent: "Intermittent",
  inactive: "Inactive",
};

const ACTIVITY_TIER_COLORS: Record<ActivityTier, string> = {
  highly_active: BRAND.green,
  active: BRAND.blue,
  intermittent: BRAND.orange,
  inactive: BRAND.red,
};

// Text colour for the pill — distinct from the fill above because the raw
// BRAND hex fails AA contrast as text in one theme or the other. See
// PILL_TEXT in lib/colors.ts.
const ACTIVITY_TIER_TEXT: Record<ActivityTier, string> = {
  highly_active: PILL_TEXT.green,
  active: PILL_TEXT.blue,
  intermittent: PILL_TEXT.orange,
  inactive: PILL_TEXT.red,
};

const ACTIVITY_TIER_ORDER: ActivityTier[] = ["highly_active", "active", "intermittent", "inactive"];

function ActivityTierPill({ tier }: { tier: ActivityTier }): React.JSX.Element {
  const fill = ACTIVITY_TIER_COLORS[tier];
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-head font-semibold whitespace-nowrap"
      style={{ backgroundColor: `${fill}1a`, color: ACTIVITY_TIER_TEXT[tier] }}
    >
      {ACTIVITY_TIER_LABELS[tier]}
    </span>
  );
}

const SUB_PERIOD_UNIT_LABEL: Record<ActivityTierBreakdown["subPeriodUnit"], string> = {
  week: "weekly",
  month: "monthly",
  quarter: "quarterly",
};

// Device-level drill-down, collapsed by default — the team breakdown above is
// the headline metric; devices are a supporting detail most people won't need.
function DeviceTierDisclosure({
  activityTiers,
}: {
  activityTiers: ActivityTierBreakdown;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const { devices, deviceCounts, insufficientHistoryDeviceCount, zeroUsageEverDeviceCount } = activityTiers;

  if (devices.length === 0) {
    return (
      <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">
        No devices with usage history yet in this window.
      </p>
    );
  }

  const sortedDevices = [...devices].sort((a, b) => a.fraction - b.fraction);
  const auditNotes = [
    insufficientHistoryDeviceCount > 0
      ? `${insufficientHistoryDeviceCount} with insufficient history`
      : null,
    zeroUsageEverDeviceCount > 0
      ? `${zeroUsageEverDeviceCount} never used (excluded)`
      : null,
  ].filter((n): n is string => n !== null);

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <button
        type="button"
        onClick={() => {
          setExpanded((e) => !e);
        }}
        className="flex items-center gap-1.5 text-xs font-head font-medium text-muted-foreground hover:text-foreground"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Device Activity Tiers
        <span className="text-muted-foreground/70 font-normal">
          {ACTIVITY_TIER_ORDER.map((tier) => `${deviceCounts[tier]} ${ACTIVITY_TIER_LABELS[tier]}`).join(
            " · ",
          )}
        </span>
      </button>
      {auditNotes.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-1">{auditNotes.join(" · ")}</p>
      )}
      {expanded && (
        <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card">
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-1.5 px-2 font-normal">Device</th>
                <th className="py-1.5 px-2 font-normal">Team</th>
                <th className="py-1.5 px-2 font-normal">Tier</th>
                <th className="py-1.5 px-2 font-normal text-right">Active / Possible</th>
              </tr>
            </thead>
            <tbody>
              {sortedDevices.map((d) => (
                <tr key={d.key} className="border-b border-border last:border-0">
                  <td className="py-1.5 px-2 truncate max-w-[140px]">{d.key}</td>
                  <td className="py-1.5 px-2 truncate max-w-[140px] text-muted-foreground">{d.teamName}</td>
                  <td className="py-1.5 px-2">
                    {d.tier ? (
                      <ActivityTierPill tier={d.tier} />
                    ) : (
                      <span className="text-muted-foreground">Insufficient history</span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right whitespace-nowrap">
                    {d.nActiveSubperiods} / {d.nPossible}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type DeltaValue = { pct: number; higherIsBetter: boolean } | null;

// ── Peer benchmark snapshot — count of metrics beating the peer average.
// The full breakdown lives in <IndustryBenchmark> further down the page;
// this is deliberately just a number + one line, paired with Health Score. ──

function countPeerWins(metrics: insights.BenchmarkResponse["metrics"]): {
  wins: number;
  total: number;
} {
  let wins = 0;
  let total = 0;
  for (const key of insights.METRIC_KEYS) {
    const stat = metrics[key];
    if (stat?.client == null) continue;
    total += 1;
    if (stat.lowerIsBetter ? stat.client < stat.peerAvg : stat.client > stat.peerAvg) {
      wins += 1;
    }
  }
  return { wins, total };
}

// ── Top/Bottom 5 Teams chart — custom Y-axis tick ─────────────────────────────

function TeamNameTick({
  x,
  y,
  payload,
  fill = "#555",
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  fill?: string;
}): React.JSX.Element {
  const name = payload?.value ?? "";
  const MAX = 24;

  let line1: string;
  let line2: string | null = null;

  if (name.length <= MAX) {
    line1 = name;
  } else {
    // Prefer splitting at ' / ' (common in combined team names)
    const slashIdx = name.indexOf(" / ");
    if (slashIdx > 0 && slashIdx <= 34) {
      line1 = name.slice(0, slashIdx);
      const rest = name.slice(slashIdx + 3);
      line2 = rest.length > MAX ? rest.slice(0, MAX - 1) + "…" : rest;
    } else {
      // Split at word boundary near MAX
      const breakAt = name.lastIndexOf(" ", MAX);
      line1 = breakAt > 0 ? name.slice(0, breakAt) : name.slice(0, MAX - 1) + "…";
      const rest = breakAt > 0 ? name.slice(breakAt + 1) : "";
      if (rest) line2 = rest.length > MAX ? rest.slice(0, MAX - 1) + "…" : rest;
    }
  }

  const LINE_H = 13;
  const yOffset = line2 ? -LINE_H / 2 : 0;

  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <text
        x={-4}
        y={yOffset}
        dy="0.355em"
        textAnchor="end"
        fill={fill}
        fontSize={10}
        fontFamily="Inter"
      >
        {line1}
      </text>
      {line2 && (
        <text
          x={-4}
          y={yOffset + LINE_H}
          dy="0.355em"
          textAnchor="end"
          fill={fill}
          fontSize={10}
          fontFamily="Inter"
        >
          {line2}
        </text>
      )}
    </g>
  );
}

// Shared renderer for the Top 5 / Bottom 5 team charts — same shape, only the
// sort order and colors differ, so the two charts read as one visual pair
// rather than looking hand-copied.
function StoreBarChart({
  data,
  baseColor,
  emphasisColor,
}: {
  data: StoreMetrics[];
  baseColor: string;
  emphasisColor: string;
}): React.JSX.Element {
  const chart = useChartTheme();
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} layout="vertical" margin={{ left: 0, right: 20, top: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chart.grid} />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fontFamily: "Inter", fill: chart.axis }}
          stroke={chart.grid}
        />
        <YAxis
          type="category"
          dataKey="store"
          width={155}
          interval={0}
          tick={<TeamNameTick fill={chart.axis} />}
          stroke={chart.grid}
        />
        <Tooltip
          formatter={(v) => {
            const num = typeof v === "number" ? v : 0;
            return [`${num.toFixed(2)}h`, "Prod Hrs/Device"];
          }}
          contentStyle={{
            fontFamily: "Inter",
            fontSize: 12,
            borderRadius: 8,
            background: chart.tooltipBg,
            border: `1px solid ${chart.tooltipBorder}`,
            color: chart.tooltipText,
          }}
        />
        <Bar dataKey="productive_hrs_per_device" radius={[0, 4, 4, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={i === 0 ? emphasisColor : baseColor} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Finding card ──────────────────────────────────────────────────────────────

type FindingType = "blue" | "orange" | "green";

const FINDING_CLASS: Record<FindingType, string> = {
  blue: "finding-blue",
  orange: "finding-orange",
  green: "finding-green",
};

function Finding({
  type = "blue",
  title,
  children,
}: {
  type?: FindingType;
  title?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={FINDING_CLASS[type]}>
      {title && <p className="font-head font-semibold text-sm mb-1">{title}</p>}
      <p className="text-sm text-foreground">{children}</p>
    </div>
  );
}

// ── Priority tier team list — collapsed to 4 with a "Show all" expander ─────
// so a tier with 30 teams doesn't blow out the summary card, but nothing is
// permanently hidden behind a dead-end "+26 more" line.

const TIER_TEAM_LIST_CAP = 4;

function TierTeamList({ storeNames }: { storeNames: string[] }): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  if (storeNames.length === 0) return null;

  const visible = expanded ? storeNames : storeNames.slice(0, TIER_TEAM_LIST_CAP);
  const overflow = storeNames.length - visible.length;

  return (
    <ul className="text-xs text-muted-foreground space-y-0.5">
      {visible.map((s) => (
        <li key={s} className="truncate">
          {s}
        </li>
      ))}
      {overflow > 0 && (
        <li>
          <button
            type="button"
            onClick={() => {
              setExpanded(true);
            }}
            className="font-medium text-phoenix-orange hover:underline"
          >
            Show all {storeNames.length}
          </button>
        </li>
      )}
      {expanded && storeNames.length > TIER_TEAM_LIST_CAP && (
        <li>
          <button
            type="button"
            onClick={() => {
              setExpanded(false);
            }}
            className="font-medium text-phoenix-orange hover:underline"
          >
            Show first {TIER_TEAM_LIST_CAP}
          </button>
        </li>
      )}
    </ul>
  );
}

// ── Auto-generated findings ───────────────────────────────────────────────────

interface FindingDef {
  type: FindingType;
  title: string;
  text: string;
}

function autoFindings(kpis: KPIs, tiers: PriorityTiers): FindingDef[] {
  return sharedFindings.autoFindings(kpis, tiers).map((f) => ({
    type: f.severity,
    title: f.title,
    text: f.text,
  }));
}

function calcDelta(current: number, prev: number | null | undefined): number | null {
  if (prev == null || prev === 0) return null;
  return ((current - prev) / Math.abs(prev)) * 100;
}

// ── Pure presentational component ────────────────────────────────────────────

export interface ExecutiveSummaryProps {
  data: reportingPeriod.DashboardData;
  prevData?: reportingPeriod.DashboardData | null;
  /** e.g. "June 2026" — label for the period `prevData` belongs to. */
  compareLabel?: string | null;
  clientId?: string;
  healthScore?: number | null;
  prevHealthScore?: number | null;
}

export function ExecutiveSummary({
  data,
  prevData = null,
  compareLabel = null,
  clientId,
  healthScore,
  prevHealthScore,
}: ExecutiveSummaryProps): React.JSX.Element {
  const { kpis, stores = [], priority_tiers: tiers } = data.methodology;
  const activityTiers = data.activity_tiers;
  const prevKpis = prevData?.methodology.kpis ?? null;
  const prevActivityTiers = prevData?.activity_tiers ?? null;

  const hBand: HealthBand | null = healthScore != null ? bandFor(healthScore) : null;
  const hMeta = hBand ? BAND_META[hBand] : null;
  const hDelta = healthScore != null && prevHealthScore != null ? healthScore - prevHealthScore : null;
  const scoreTooltipId = useId();
  const top5TitleId = useId();
  const bottom5TitleId = useId();

  // Shares its query cache with <IndustryBenchmark> further down the page —
  // same clientId + month key, so this doesn't add a second network request.
  const benchmark = useBenchmark(clientId ?? "", null);
  const peerData = benchmark.data;
  const showPeerSnapshot =
    Boolean(clientId) &&
    !benchmark.isLoading &&
    !benchmark.error &&
    peerData !== undefined &&
    peerData.industries.length > 0 &&
    peerData.peerCount > 0;
  const peerWins = peerData ? countPeerWins(peerData.metrics) : { wins: 0, total: 0 };

  const d = (key: keyof KPIs, higherIsBetter: boolean = true): DeltaValue =>
    prevKpis ? { pct: calcDelta(kpis[key], prevKpis[key]) ?? 0, higherIsBetter } : null;

  const adoptionDeltaValue: DeltaValue = prevActivityTiers
    ? { pct: calcDelta(activityTiers.activeOrBetterPct, prevActivityTiers.activeOrBetterPct) ?? 0, higherIsBetter: true }
    : null;

  const kpiTiles: KPITile[] = [
    {
      label: "Total Sessions",
      value: fmtInt(kpis.total_sessions),
      delta: d("total_sessions"),
      compareValue: prevKpis ? fmtInt(prevKpis.total_sessions) : undefined,
    },
    {
      label: "Productive Hours",
      value: fmtHours(kpis.productive_hours),
      info: "8h credit applied to extended sessions",
      delta: d("productive_hours"),
      compareValue: prevKpis ? fmtHours(prevKpis.productive_hours) : undefined,
    },
    {
      label: "Extended Session Rate",
      value: fmtPct(kpis.extended_session_rate),
      color:
        kpis.extended_session_rate >= 40
          ? BRAND.red
          : kpis.extended_session_rate >= 25
            ? BRAND.orange
            : kpis.extended_session_rate >= 10
              ? BRAND.blue
              : BRAND.green,
      delta: d("extended_session_rate", false),
      compareValue: prevKpis ? fmtPct(prevKpis.extended_session_rate) : undefined,
    },
    {
      label: "Fleet Idle Rate",
      value: fmtPct(kpis.fleet_idle_rate_pct),
      color:
        kpis.fleet_idle_rate_pct >= 75
          ? BRAND.red
          : kpis.fleet_idle_rate_pct >= 50
            ? BRAND.orange
            : kpis.fleet_idle_rate_pct >= 25
              ? BRAND.blue
              : BRAND.green,
      delta: d("fleet_idle_rate_pct", false),
      compareValue: prevKpis ? fmtPct(prevKpis.fleet_idle_rate_pct) : undefined,
    },
    {
      label: "Active Teams",
      value: `${kpis.active_stores} / ${kpis.total_stores}`,
      info: "teams with ≥1 valid session",
      delta: d("active_stores"),
      compareValue: prevKpis ? `${prevKpis.active_stores} / ${prevKpis.total_stores}` : undefined,
    },
    {
      label: "Network Adoption",
      value: fmtPct(activityTiers.activeOrBetterPct),
      info: "share of teams Active or better",
      color:
        activityTiers.activeOrBetterPct >= 80
          ? BRAND.green
          : activityTiers.activeOrBetterPct >= 50
            ? BRAND.orange
            : BRAND.red,
      delta: adoptionDeltaValue,
      compareValue: prevActivityTiers ? fmtPct(prevActivityTiers.activeOrBetterPct) : undefined,
    },
    {
      label: "Total Hours",
      value: fmtHours(kpis.total_hours),
      delta: d("total_hours"),
      compareValue: prevKpis ? fmtHours(prevKpis.total_hours) : undefined,
    },
    {
      label: "Micro-Sessions Excluded",
      value: fmtInt(kpis.micro_sessions_excluded),
      info: "sessions <2 min removed",
    },
  ];

  const top5: StoreMetrics[] = [...stores]
    .sort((a, b) => b.productive_hrs_per_device - a.productive_hrs_per_device)
    .slice(0, 5);

  // Excludes fully-idle teams (0 hrs/device) — on most networks those are
  // the majority and tied at zero, so an unfiltered ascending sort just
  // picks an arbitrary 5 of them: a wall of invisible bars, not a "worst
  // performers" view. Zero-usage teams are already covered by the idle
  // device / Activity Tier findings elsewhere on this page. Also excludes
  // anything already in Top 5 so a small network never shows the same
  // teams in both charts.
  const top5Names = new Set(top5.map((s) => s.store));
  const bottom5: StoreMetrics[] = [...stores]
    .filter((s) => s.productive_hrs_per_device > 0 && !top5Names.has(s.store))
    .sort((a, b) => a.productive_hrs_per_device - b.productive_hrs_per_device)
    .slice(0, 5);

  const findings = autoFindings(kpis, tiers);

  if (prevKpis) {
    const comparePeriodPhrase = compareLabel ?? "last period";
    const sessionDelta = calcDelta(kpis.total_sessions, prevKpis.total_sessions);
    const hoursDelta = calcDelta(kpis.productive_hours, prevKpis.productive_hours);
    const adoptionDelta = prevActivityTiers
      ? calcDelta(activityTiers.activeOrBetterPct, prevActivityTiers.activeOrBetterPct)
      : null;

    if (sessionDelta !== null && Math.abs(sessionDelta) >= 5) {
      const drop = sessionDelta < 0;
      findings.unshift({
        type: drop ? "orange" : "green",
        title: `Sessions ${drop ? "down" : "up"} ${Math.abs(sessionDelta).toFixed(1)}% vs ${comparePeriodPhrase}`,
        text: `${fmtInt(kpis.total_sessions)} sessions this period vs ${fmtInt(prevKpis.total_sessions)} in ${comparePeriodPhrase}.`,
      });
    }
    if (hoursDelta !== null && Math.abs(hoursDelta) >= 5) {
      const drop = hoursDelta < 0;
      findings.unshift({
        type: drop ? "orange" : "green",
        title: `Productive hours ${drop ? "down" : "up"} ${Math.abs(hoursDelta).toFixed(1)}% vs ${comparePeriodPhrase}`,
        text: `${fmtHours(kpis.productive_hours)} this period vs ${fmtHours(prevKpis.productive_hours)} in ${comparePeriodPhrase}.`,
      });
    }
    if (adoptionDelta !== null && prevActivityTiers && Math.abs(adoptionDelta) >= 5) {
      const drop = adoptionDelta < 0;
      findings.unshift({
        type: drop ? "orange" : "green",
        title: `Network adoption ${drop ? "down" : "up"} ${Math.abs(adoptionDelta).toFixed(1)}% vs ${comparePeriodPhrase}`,
        text: `${fmtPct(activityTiers.activeOrBetterPct)} adoption this period vs ${fmtPct(prevActivityTiers.activeOrBetterPct)} in ${comparePeriodPhrase}.`,
      });
    }
  }

  const TIER_ORDER = ["high", "watch_list", "on_track", "excellent"] as const;

  return (
    <div className="space-y-6">
      {healthScore != null && hBand != null && hMeta != null && (
        <div
          className={
            showPeerSnapshot ? "grid grid-cols-1 lg:grid-cols-2 gap-[10px] items-start" : ""
          }
        >
          <div className="card-duress p-5">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="section-label">Customer Health Score</span>
              <div
                tabIndex={0}
                aria-describedby={scoreTooltipId}
                className="relative group inline-flex items-center rounded-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <Info className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help transition-colors" />
                <div
                  id={scoreTooltipId}
                  role="tooltip"
                  className="absolute left-0 top-full mt-2 z-50 w-72 rounded-card border border-border bg-card shadow-card p-3 opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-focus:opacity-100 group-focus-within:opacity-100"
                >
                  <p className="text-xs font-semibold text-foreground mb-2">How the score is calculated</p>
                  <div className="space-y-2">
                    {[
                      { label: "Network Adoption", weight: "35%" },
                      { label: "Session Quality", weight: "30%", note: "fewer extended sessions = higher" },
                      { label: "Fleet Idle Rate", weight: "20%", note: "fewer idle devices = higher" },
                      { label: "Check-in Completion", weight: "15%", note: "when data is available" },
                      { label: "CSAT Survey", weight: "15%", note: "most recent survey, when available" },
                    ].map(({ label, weight, note }) => (
                      <div key={label} className="flex items-start justify-between gap-2">
                        <div>
                          <span className="text-xs text-foreground">{label}</span>
                          {note && <span className="block text-[10px] text-muted-foreground">{note}</span>}
                        </div>
                        <span className="text-xs font-semibold text-foreground shrink-0">{weight}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2.5 pt-2 border-t border-border">
                    Weights normalise when signals are missing — the score is always on the full 0–100 scale.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-baseline gap-3">
              <span className={cn("num text-[48px] leading-none font-semibold", hMeta.textClass)}>
                {healthScore}
              </span>
              <div>
                <div className={`text-sm font-medium ${hMeta.textClass}`}>{hMeta.label}</div>
                {hDelta !== null && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    {hDelta >= 0 ? (
                      <ArrowUp className="h-3 w-3 text-pill-green" />
                    ) : (
                      <ArrowDown className="h-3 w-3 text-pill-red" />
                    )}
                    {Math.abs(hDelta)} pts vs previous period
                  </div>
                )}
              </div>
            </div>
          </div>

          {showPeerSnapshot && (
            <div className="card-duress p-5">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="section-label">vs Industry Peers</span>
              </div>
              <div className="flex items-baseline gap-3">
                <span className="num text-[48px] leading-none font-semibold text-foreground">
                  {peerWins.wins}
                  <span className="text-2xl text-muted-foreground">/{peerWins.total}</span>
                </span>
                <div>
                  <div className="text-sm font-medium text-foreground">metrics beating peer average</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {peerData.industries.join(", ")} · {peerData.peerCount}{" "}
                    {peerData.peerCount === 1 ? "peer" : "peers"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <KPIGrid tiles={kpiTiles} compareLabel={prevKpis ? compareLabel : null} />

      {/* Activity tier summary — the recurrence-based reading behind the
          redefined Network Adoption % above. */}
      <div className="card-duress p-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="font-head font-semibold text-sm text-foreground">Team Activity Tiers</p>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {SUB_PERIOD_UNIT_LABEL[activityTiers.subPeriodUnit]} cadence · {activityTiers.totalSubPeriods}{" "}
            sub-periods
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          How consistently a team is actually used, not just whether it's been used once — a team
          needs to be Active or better most sub-periods to count toward Network Adoption. Every team
          the client has is counted, including ones with no usage at all; only teams marked excluded
          are left out.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {ACTIVITY_TIER_ORDER.map((tier) => (
            <div key={tier}>
              <div className="flex items-center gap-2 mb-2">
                <ActivityTierPill tier={tier} />
                <span className="font-head font-semibold text-lg">{activityTiers.teamCounts[tier]}</span>
              </div>
            </div>
          ))}
        </div>
        <DeviceTierDisclosure activityTiers={activityTiers} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px] items-start">
        {/* Top 5 / Bottom 5 teams bar charts */}
        <div className="card-duress p-5">
          <p id={top5TitleId} className="font-head font-semibold text-sm text-foreground mb-4">
            Top 5 Teams — Productive Hrs/Device
          </p>
          <div role="img" aria-labelledby={top5TitleId}>
            <StoreBarChart data={top5} baseColor={BRAND.blue} emphasisColor={BRAND.green} />
          </div>

          {bottom5.length > 0 && (
            <>
              <p id={bottom5TitleId} className="font-head font-semibold text-sm text-foreground mt-6 mb-4">
                Bottom 5 Teams — Productive Hrs/Device
              </p>
              <div role="img" aria-labelledby={bottom5TitleId}>
                <StoreBarChart data={bottom5} baseColor={BRAND.orange} emphasisColor={BRAND.red} />
              </div>
            </>
          )}
        </div>

        {/* Key findings */}
        <div className="card-duress p-5">
          <p className="font-head font-semibold text-sm text-foreground mb-4">Key Findings</p>
          <div className="space-y-3">
            {findings.map((f, i) => (
              <Finding key={i} type={f.type} title={f.title}>
                {f.text}
              </Finding>
            ))}
          </div>
        </div>
      </div>

      {clientId ? <IndustryBenchmark clientId={clientId} /> : null}

      {/* Priority tier summary */}
      <div className="card-duress p-5">
        <p className="font-head font-semibold text-sm text-foreground mb-4">
          Priority Tier Summary
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {TIER_ORDER.map((tier) => (
            <div key={tier}>
              <div className="flex items-center gap-2 mb-2">
                <TierBadge tier={tier} />
                <span className="font-head font-semibold text-lg">{tiers[tier].length}</span>
              </div>
              <TierTeamList storeNames={tiers[tier]} />
            </div>
          ))}
        </div>
      </div>

      {/* Methodology note */}
      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          <strong>Productive Hours</strong> reflect the 8-hour credit applied to any session ≥24h,
          rather than the raw duration. Sessions under 2 minutes are excluded as micro-sessions
          before any calculation. Productive Hrs/Device is the primary efficiency comparator — it
          normalises for device count.
        </p>
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

export function ExecutiveSummaryTab({
  clientId,
  view,
  compareView = null,
  compareLabel = null,
  healthScore,
  prevHealthScore,
}: {
  clientId: string;
  view: SelectedView | null;
  compareView?: SelectedView | null;
  compareLabel?: string | null;
  healthScore?: number | null;
  prevHealthScore?: number | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);
  const { data: compareData } = useDashboardView(clientId, compareView);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return (
    <ExecutiveSummary
      data={data}
      prevData={compareData ?? null}
      compareLabel={compareLabel}
      clientId={clientId}
      healthScore={healthScore ?? null}
      prevHealthScore={prevHealthScore ?? null}
    />
  );
}
